import { Box, Button, Stack, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { CenterModal } from "../CenterModal";

interface ArchiveConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  confirmPending: boolean;
  title: string;
  description: string;
  /** When set, replaces the default destructive action label (e.g. archive). */
  confirmButtonLabel?: string;
}

/**
 * Reusable destructive confirmation modal for archive actions.
 * Figma 514:26509 — Cancel (tertiary) + Archive (secondary destructive, red border).
 * Backdrop click does not close the modal.
 */
export function ArchiveConfirmModal({
  open,
  onClose,
  onConfirm,
  confirmPending,
  title,
  description,
  confirmButtonLabel,
}: ArchiveConfirmModalProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <CenterModal
      open={open}
      onClose={onClose}
      title={title}
      disableBackdropClick
      width={{ xs: "min(100% - 40px, 320px)", md: 480 }}
      hideCloseButton={false}
      closeButtonSx={{
        display: { xs: "inline-flex", md: "none" },
      }}
      titleSx={{
        flex: { xs: 1, md: "unset" },
        minWidth: { xs: 0, md: "auto" },
        fontSize: { xs: "1.5rem", md: "2rem" },
        letterSpacing: { xs: "-0.12px", md: "-0.02em" },
      }}
      paperSx={{
        border: "1px solid",
        borderColor: theme.palette.custom.gray[200],
        borderRadius: {
          xs: "8px",
          md: "var(--Card-Radius-Large)",
        },
        boxShadow: "0px 2px 40px rgba(0, 0, 0, 0.1)",
        py: { xs: 1, md: 2 },
      }}
      headerSx={{
        alignItems: "flex-start",
        pb: 0,
        pt: { xs: 1.5, md: 2 },
      }}
      contentSx={{
        pt: { xs: 1.5, md: 1 },
        px: { xs: 0, md: 3 },
        pb: { xs: 0, md: 2 },
      }}
    >
      <Stack spacing={2}>
        <Box sx={{ px: { xs: 2.5, md: 0 } }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.custom.gray[700],
              fontSize: "0.875rem",
              lineHeight: 1.5,
              fontWeight: 400,
            }}
          >
            {description}
          </Typography>
        </Box>
        <Box sx={{ px: { xs: 2.5, md: 0 }, py: { xs: 1, md: 0 } }}>
          <Stack direction="row" spacing={2} sx={{ width: "100%" }}>
            <Button
              variant="text"
              onClick={onClose}
              sx={{
                flex: 1,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                lineHeight: 1.5,
                minHeight: { xs: 44, md: 52 },
                py: { xs: "4px", md: 1 },
                px: { xs: 2, md: 3 },
                color: theme.palette.custom.primary,
                borderRadius: "8px",
                "&:hover": {
                  bgcolor: theme.palette.custom.primarySecondaryHover,
                },
              }}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="outlined"
              onClick={onConfirm}
              disabled={confirmPending}
              sx={{
                flex: 1,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "1rem",
                lineHeight: 1.5,
                minHeight: { xs: 44, md: 52 },
                py: { xs: "4px", md: 1 },
                px: { xs: 2, md: 3 },
                borderRadius: "8px",
                color: theme.palette.custom.destructive,
                borderColor: theme.palette.custom.destructive,
                bgcolor: "white",
                "&:hover": {
                  borderColor: theme.palette.custom.destructive,
                  bgcolor: theme.palette.custom.destructiveSecondaryHover,
                },
                "&.Mui-disabled": {
                  color: theme.palette.custom.destructive,
                  borderColor: theme.palette.custom.destructive,
                  opacity: 0.5,
                },
              }}
            >
              {confirmButtonLabel ?? t("task.archive")}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </CenterModal>
  );
}
